import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppDispatch, RootState } from '../store'

import { arrayDifference, timelineToMessages, uniqueArray } from '../utilities'

type DMStore = Record<string, DM>

type DMState = {
  dms: DMStore
  originalList: DMStore
  queueLoaded: boolean
  selectedConvo: string | null
  chatName: string
  chatType: string
}

const initialState: DMState = {
  dms: {},
  originalList: {},
  queueLoaded: false,
  selectedConvo: null,
  chatName: '',
  chatType: 'Private Chat'
}

const dmsSlice = createSlice({
  name: 'dms',
  initialState,
  reducers: {
    setQueueLoaded: (state: DMState) => {
      state.queueLoaded = true
    },

    patchMessage: (state: DMState, action: PayloadAction<any>) => {
      const msg = action.payload
      const { sessionId, userId } = msg

      if (!sessionId && !userId) {
        return
      }

      Object.values(state.dms).forEach(session => {
        let changed = false

        // Update instances of this message
        const modifiedMessages = session.messages.map(message => {
          const toReturn = { ...message }

          if (message.id === msg.id) {
            toReturn.content = msg.content.body
            toReturn.tags = msg.tags
            toReturn.meta.body_history = msg.content.meta.body_history
            toReturn.meta.reactions = msg.content?.meta?.reactions ?? []
            changed = true
          }

          return toReturn
        })

        // Updated stored messages if a change occurs
        if (changed) {
          session.messages = modifiedMessages
        }
      })
    },

    setSelected: (state: DMState, action: PayloadAction<string | null>) => {
      state.selectedConvo = action.payload
    },

    removeDM: (state: DMState, action: PayloadAction<string>) => {
      const id = action.payload
      delete state.dms[id]
      delete state.originalList[id]
    },

    updateMsgSeen: (state: DMState, action: PayloadAction<string>) => {
      if (state.dms[action.payload]) state.dms[action.payload].isNew = false
    },

    setType: (
      state: DMState,
      action: PayloadAction<{ id: string; newType: string }>
    ) => {
      const { id, newType } = action.payload
      state.dms[id].type = newType
    },

    setDMs: (
      state: DMState,
      action: PayloadAction<{ id: string; session: DM; isGroup: boolean }>
    ) => {
      const { id, session, isGroup } = action.payload

      const newSession: DM = { ...session, isGroup }
      const oldSession = state.dms[id]

      if (!oldSession) {
        // Initial add of dm
        const toAdd: DM = { ...newSession, isNew: newSession.isNew ?? true }
        state.dms[id] = toAdd
        state.originalList[id] = toAdd
        return
      }

      // Ignore old updates
      if (new Date(newSession.lastUpdate) < new Date(oldSession.lastUpdate)) {
        return
      }

      const combined: DM = {
        ...oldSession,
        ...newSession,
        messages: [...(oldSession.messages ?? []), ...newSession.messages],
        fetched: oldSession.fetched,
        users: oldSession.users ?? [id]
      }

      state.dms[id] = combined
      state.originalList[id] = combined
    },

    updateMessages: (state: DMState, action: PayloadAction<DMStore>) => {
      state.dms = action.payload
    },

    updateDMs: (
      state: DMState,
      action: PayloadAction<{ id: string; fetched: boolean; messages: any[] }>
    ) => {
      const { id, fetched, messages } = action.payload
      state.dms[id].fetched = fetched
      state.dms[id].messages = messages
    },

    updateDmMessages: (
      state: DMState,
      action: PayloadAction<{ id: string; messages: any[] }>
    ) => {
      const { id, messages } = action.payload
      state.dms[id].messages = messages
      state.dms[id].isNew = true
    },

    updateSingleDm: (
      state: DMState,
      action: PayloadAction<{ id: string; dm: any }>
    ) => {
      const { id, dm } = action.payload
      state.dms[id] = { ...dm }
    },

    updateDmSingleMessage: (
      state: DMState,
      action: PayloadAction<{ id: string; index: number; message: any }>
    ) => {
      const { id, index, message } = action.payload
      if (id !== undefined && index !== undefined && message !== undefined) {
        state.dms[id].messages[index] = { ...message }
      }
    },

    appendDMs: (
      state: DMState,
      action: PayloadAction<{ id: string; messages: any[] }>
    ) => {
      const { id, messages } = action.payload
      state.dms[id].messages = [...(state.dms[id].messages ?? []), ...messages]
    },

    filterDMs: (state: DMState, action: PayloadAction<string>) => {
      const filter = action.payload
      const { originalList } = state

      let filtered = {}
      switch (filter) {
        case 'All Chats':
          state.dms = originalList
          return
        case 'Private Only':
          for (const key in originalList) {
            if (originalList[key].users?.length === 2) {
              filtered = { ...filtered, [key]: originalList[key] }
            }
          }
          break
        case 'Group Only':
          for (const key in originalList) {
            if (originalList[key].users?.length > 2) {
              filtered = { ...filtered, [key]: originalList[key] }
            }
          }
          break
        case 'Closed Group':
          break
        default:
          throw new Error(`Selected filter does not exist`)
      }

      state.dms = filtered
    },

    setChatType: (state: DMState, action: PayloadAction<string>) => {
      state.chatType = action.payload
    },

    setChatName: (state: DMState, action: PayloadAction<string>) => {
      state.chatName = action.payload
    },

    addMembers: (
      state: DMState,
      action: PayloadAction<{ id: string; users: any[] }>
    ) => {
      const { id, users } = action.payload

      if (!(id in state.dms)) {
        return
      }

      let newUsers = []
      if (Array.isArray(state.dms[id].users)) {
        newUsers = uniqueArray(...state.dms[id].users, ...users)
      } else {
        newUsers = users
      }

      state.dms[id].users = newUsers
      state.dms[id].userIds = newUsers
    },

    removeMembers: (
      state: DMState,
      action: PayloadAction<{ id: string; users: any[] }>
    ) => {
      const { id, users } = action.payload

      if (!(id in state.dms)) {
        return
      }

      let newUsers = []
      if (Array.isArray(state.dms[id].users)) {
        newUsers = arrayDifference(state.dms[id].users, users)
      } else {
        newUsers = []
      }

      state.dms[id].users = newUsers
      state.dms[id].userIds = newUsers
    },

    deleteMessage: (state: DMState, action: PayloadAction<string>) => {
      const id = action.payload

      Object.values(state.dms).forEach(dm => {
        dm.messages = dm.messages.filter(msg => msg.id !== id)
      })
    },

    handlePartKick: (
      state: DMState,
      action: PayloadAction<{ msg: any; kicked: boolean }>
    ) => {
      const { msg, kicked } = action.payload
      const { sessionId, content, id } = msg

      const newMsg = {
        content: content.body,
        media: content.media,
        sender: content.user.name,
        senderFull: content.user,
        meta: content.meta,
        session_id: sessionId,
        tags: [],
        type: kicked ? 'kick' : 'part',
        id,
        date: new Date()
      }

      state.dms[sessionId].messages.unshift(newMsg)
      state.dms[sessionId].users = [...state.dms[sessionId].users].filter(
        id => id !== content.user.id
      )
    },

    handleUpdateUsers: (
      state: DMState,
      action: PayloadAction<{ users: any[]; session: string }>
    ) => {
      const { users, session } = action.payload
      state.dms[session].userIds = users
      state.dms[session].users = users
    }
  }
})

export const {
  setQueueLoaded,
  patchMessage,
  setSelected,
  removeDM,
  updateMsgSeen,
  setType,
  setDMs,
  updateMessages,
  updateDMs,
  updateDmMessages,
  updateDmSingleMessage,
  updateSingleDm,
  appendDMs,
  filterDMs,
  setChatType,
  setChatName,
  addMembers,
  removeMembers,
  deleteMessage,
  handlePartKick,
  handleUpdateUsers
} = dmsSlice.actions

export const addGroupChat = (obj: any) => {
  return async (dispatch: AppDispatch) => {
    const { name, id, createdAt, updatedAt, userIds, latest_activity } =
      obj as DM

    const messages = timelineToMessages(latest_activity || [])
    const session: DM = {
      ...obj,
      name,
      type: 'Live',
      messages,
      user: null,
      isNew: false,
      comments: [],
      fetched: false,
      session: id,
      startDate: createdAt ?? new Date().toISOString(),
      lastUpdate: updatedAt ?? new Date().toISOString(),
      users: userIds,
      closed: false,
      latest_activity
    }
    dispatch(setDMs({ id: obj.id, session, isGroup: true }))
  }
}

export const selectDMs = (state: RootState) => state.dms

export default dmsSlice.reducer
